import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, CircularProgress, Typography, Button } from '@mui/material';

import { loginSuccess } from '../store/slices/authSlice';
import { decodeNavParam, saveSsoNav } from '../utils/ssoNav';

/**
 * Aterrissagem do SSO vindo da Plataforma Verde.
 *
 * O token chega no fragmento (`/sso#token=...`) e não na query: fragmento não
 * é enviado ao servidor, então não entra em log de acesso nem no cabeçalho
 * Referer de recursos externos. Aqui ele é trocado pelos dados do usuário e
 * apagado da barra de endereços.
 */
export const SsoPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [erro, setErro] = useState<string | null>(null);
  const jaProcessou = useRef(false);

  useEffect(() => {
    // StrictMode monta duas vezes em dev; sem isso o token seria consumido
    // e a segunda passagem falharia.
    if (jaProcessou.current) return;
    jaProcessou.current = true;

    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const token = hash.get('token');

    // O auth redireciona para cá com #error quando o token é recusado (ex.:
    // link expirado por demora ao abrir a aba). Mensagem amigável em vez de JSON.
    if (hash.get('error')) {
      window.history.replaceState(null, '', '/sso');
      setErro('Seu link de acesso expirou. Volte à Plataforma Verde e clique novamente em Legislação.');
      return;
    }

    if (!token) {
      setErro('Link de acesso inválido ou já utilizado.');
      return;
    }

    // Origem/URLs de retorno (opcional): guarda para o Layout montar o botão
    // de voltar, o perfil e o "Sair". Ausente em token antigo — segue sem eles.
    const navParam = hash.get('nav');
    if (navParam) {
      saveSsoNav(decodeNavParam(navParam));
    }

    // Tira o token da URL antes de qualquer await: evita que ele sobreviva no
    // histórico se o usuário fechar no meio do carregamento.
    window.history.replaceState(null, '', '/sso');

    (async () => {
      try {
        const res = await fetch('/auth/me', {
          headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const user = await res.json();

        dispatch(
          loginSuccess({
            token,
            user: {
              id: user.id,
              email: user.email,
              name: user.name,
              role: user.role,
              isActive: user.isActive ?? user.is_active ?? true,
            },
          })
        );
        navigate('/dashboard', { replace: true });
      } catch {
        setErro('Não foi possível entrar. Volte à Plataforma Verde e clique novamente.');
      }
    })();
  }, [dispatch, navigate]);

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        px: 3,
        textAlign: 'center',
      }}
    >
      {erro ? (
        <>
          <Typography variant="h6">{erro}</Typography>
          <Button variant="contained" onClick={() => navigate('/login', { replace: true })}>
            Ir para o login
          </Button>
        </>
      ) : (
        <>
          <CircularProgress />
          <Typography color="text.secondary">Entrando…</Typography>
        </>
      )}
    </Box>
  );
};
